import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';

const ROLE_BADGE = {
  landlord: 'bg-primary/10 text-primary',
  tenant: 'bg-accent/10 text-accent',
  employee: 'bg-amber-100 text-amber-800',
};

export function UsersTable({ users, currentUserId, onDelete }) {
  return (
    <Card className="overflow-hidden border border-border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-4 font-medium">Name</th>
              <th className="text-left p-4 font-medium">Email</th>
              <th className="text-left p-4 font-medium">Role</th>
              <th className="text-left p-4 font-medium">Phone</th>
              <th className="text-left p-4 font-medium">Joined</th>
              <th className="text-right p-4 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, idx) => (
              <tr key={u.id} className="border-t border-border" data-testid={`user-row-${idx}`}>
                <td className="p-4">{u.name}</td>
                <td className="p-4 text-muted-foreground">{u.email}</td>
                <td className="p-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${ROLE_BADGE[u.role] || 'bg-muted text-foreground'}`}>{u.role}</span>
                </td>
                <td className="p-4 text-muted-foreground">{u.phone || '-'}</td>
                <td className="p-4 text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="p-4 text-right">
                  {u.id !== currentUserId && (
                    <Button
                      data-testid={`delete-user-btn-${u.id}`}
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => onDelete(u)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function PropertiesTable({ properties, onView }) {
  return (
    <Card className="overflow-hidden border border-border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-4 font-medium">Title</th>
              <th className="text-left p-4 font-medium">Landlord</th>
              <th className="text-left p-4 font-medium">City</th>
              <th className="text-left p-4 font-medium">Price</th>
              <th className="text-left p-4 font-medium">Discount</th>
              <th className="text-left p-4 font-medium">Status</th>
              <th className="text-left p-4 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {properties.map((p, idx) => (
              <tr key={p.id} className="border-t border-border" data-testid={`property-row-${idx}`}>
                <td className="p-4 font-medium">{p.title}</td>
                <td className="p-4 text-muted-foreground">{p.landlord_name}</td>
                <td className="p-4 text-muted-foreground">{p.city}</td>
                <td className="p-4">${p.price}</td>
                <td className="p-4">
                  {p.discount_rate > 0 && (
                    <span className="px-2 py-1 rounded-full text-xs font-bold bg-accent/10 text-accent">-{p.discount_rate}%</span>
                  )}
                </td>
                <td className="p-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${p.status === 'available' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{p.status}</span>
                </td>
                <td className="p-4">
                  <Button data-testid={`view-property-${idx}-btn`} onClick={() => onView(p.id)} variant="outline" size="sm" className="rounded-full">View</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function LeasesTable({ leases, statusChip, onView, onDelete }) {
  return (
    <Card className="overflow-hidden border border-border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-4 font-medium">Property</th>
              <th className="text-left p-4 font-medium">Tenant</th>
              <th className="text-left p-4 font-medium">Landlord</th>
              <th className="text-left p-4 font-medium">Period</th>
              <th className="text-left p-4 font-medium">Rent</th>
              <th className="text-left p-4 font-medium">Status</th>
              <th className="text-left p-4 font-medium">Created</th>
              <th className="text-left p-4 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leases.length === 0 ? (
              <tr><td colSpan="8" className="p-8 text-center text-muted-foreground">No leases yet</td></tr>
            ) : leases.map((l, idx) => (
              <tr key={l.id} className="border-t border-border" data-testid={`lease-row-${idx}`}>
                <td className="p-4 font-medium">{l.property_title}</td>
                <td className="p-4 text-muted-foreground">{l.tenant_name}</td>
                <td className="p-4 text-muted-foreground">{l.landlord_name}</td>
                <td className="p-4 text-sm text-muted-foreground">{l.start_date} → {l.end_date}</td>
                <td className="p-4">${l.monthly_rent}</td>
                <td className="p-4">{statusChip(l.status)}</td>
                <td className="p-4 text-muted-foreground text-sm">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="p-4 flex gap-2">
                  <Button data-testid={`view-lease-${idx}-btn`} onClick={() => onView(l.id)} variant="outline" size="sm" className="rounded-full">View</Button>
                  <Button data-testid={`delete-lease-${idx}-btn`} onClick={() => onDelete(l.id, l.tenant_name, l.property_title)} variant="outline" size="sm" className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function ApplicationsTable({ submissions, statusChip, onDelete }) {
  return (
    <Card className="overflow-hidden border border-border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-4 font-medium">Form</th>
              <th className="text-left p-4 font-medium">Tenant</th>
              <th className="text-left p-4 font-medium">Landlord</th>
              <th className="text-left p-4 font-medium">Property</th>
              <th className="text-left p-4 font-medium">Status</th>
              <th className="text-left p-4 font-medium">Submitted</th>
              <th className="text-left p-4 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {submissions.length === 0 ? (
              <tr><td colSpan="7" className="p-8 text-center text-muted-foreground">No applications yet</td></tr>
            ) : submissions.map((s, idx) => (
              <tr key={s.id} className="border-t border-border" data-testid={`submission-row-${idx}`}>
                <td className="p-4 font-medium">{s.template_title}</td>
                <td className="p-4 text-muted-foreground">{s.tenant_name}</td>
                <td className="p-4 text-muted-foreground">{s.landlord_name || '-'}</td>
                <td className="p-4 text-muted-foreground">{s.property_title || '-'}</td>
                <td className="p-4">{statusChip(s.status)}</td>
                <td className="p-4 text-muted-foreground text-sm">{new Date(s.created_at).toLocaleDateString()}</td>
                <td className="p-4">
                  <Button data-testid={`delete-submission-${idx}-btn`} onClick={() => onDelete(s.id, s.template_title, s.tenant_name)} variant="outline" size="sm" className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
